import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Target, Heart, Compass, Sparkles, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function IntentionSettingGuide() {
  const navigate = useNavigate();

  const sampleIntentions = [
    "I intend to prioritize my health and well-being above all else",
    "I intend to speak kindly to myself every day",
    "I intend to move my body in ways that bring me joy",
    "I intend to nourish myself with wholesome foods",
    "I intend to create space for rest and recovery",
    "I intend to build deeper connections with the people I love",
    "I intend to embrace growth, even when it feels uncomfortable",
    "I intend to celebrate my progress, no matter how small"
  ];

  const questions = [
    "What does my ideal life look like in 12 weeks?",
    "How do I want to feel in my body every day?",
    "What habits no longer serve me?",
    "What am I ready to release?",
    "What do I want more of in my life?",
    "Who do I want to become through this journey?",
    "What would make me proud at the end of this program?",
    "What is my deepest 'why' for starting this?"
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4 text-secondary-700 hover:text-primary">
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-semibold mb-4">
            Week 1 • Day 1 Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            Welcome & Intention Setting
          </h1>
          <p className="text-xl text-secondary-600">Begin Your Transformation with Clarity and Purpose</p>
        </div>

        {/* Hero Section */}
        <section className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl p-8 text-white mb-8">
          <div className="flex items-start gap-4">
            <Compass className="w-12 h-12 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-2xl mb-2">Your 12-Week Journey Begins Now</h3>
              <p className="text-purple-100 text-lg">
                This moment matters. The intentions you set today will be your compass for the next 12 weeks. 
                Take this seriously—your future self is counting on you.
              </p>
            </div>
          </div>
        </section>

        {/* Why Intentions Matter */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">Why Intentions Matter</h2>
          <p className="text-secondary-700 mb-4">
            Intentions are different from goals. While goals focus on specific outcomes, intentions focus on how you want to <em>be</em> and <em>feel</em>. 
            They create a foundation for your actions and help you stay aligned with your values even when life gets chaotic.
          </p>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { title: "50% More Motivation", desc: "Studies show people with clear intentions are more likely to follow through" },
              { title: "Reduced Anxiety", desc: "Knowing your 'why' provides calm during uncertain moments" },
              { title: "Better Decisions", desc: "Intentions become a filter for daily choices" }
            ].map((item, i) => (
              <div key={i} className="text-center p-4 bg-purple-50 rounded-xl">
                <p className="text-xl font-bold text-purple-600 mb-1">{item.title}</p>
                <p className="text-sm text-secondary-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* The Practice */}
        <section className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 border-2 border-purple-200 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">Today's Practice</h2>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">1</div>
              <div>
                <h3 className="font-bold text-secondary-900">Find Your Quiet Space (2 min)</h3>
                <p className="text-secondary-600">Sit somewhere comfortable where you won't be disturbed. Turn off your phone. Take 5 deep breaths.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">2</div>
              <div>
                <h3 className="font-bold text-secondary-900">Close Your Eyes & Visualize (5 min)</h3>
                <p className="text-secondary-600">Picture yourself 12 weeks from now. What does she look like? How does she feel? What is she doing differently?</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">3</div>
              <div>
                <h3 className="font-bold text-secondary-900">Ask Yourself the Deep Questions (5 min)</h3>
                <p className="text-secondary-600">Use the reflection questions below to dig deep into your motivations.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">4</div>
              <div>
                <h3 className="font-bold text-secondary-900">Speak Your Intentions Aloud (3 min)</h3>
                <p className="text-secondary-600">Stand up, open your eyes, and speak your intentions out loud. Hearing your own voice claim your desires makes them real.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Reflection Questions */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4 flex items-center gap-2">
            <Heart className="w-6 h-6 text-pink-500" />
            Reflection Questions
          </h2>
          <div className="grid md:grid-cols-2 gap-3">
            {questions.map((q, index) => (
              <div key={index} className="bg-purple-50 rounded-lg p-4 flex items-start gap-2">
                <Target className="w-4 h-4 text-purple-500 mt-1 flex-shrink-0" />
                <span className="text-secondary-700">{q}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Sample Intentions */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-amber-500" />
            Sample Intentions for Inspiration
          </h2>
          <p className="text-secondary-600 mb-4">Feel free to use these as-is or adapt them to your unique journey:</p>
          <div className="grid gap-3">
            {sampleIntentions.map((intention, index) => (
              <div key={index} className="flex items-center gap-3 p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-purple-500 flex-shrink-0" />
                <span className="text-secondary-700 italic">"{intention}"</span>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export default IntentionSettingGuide;
